import Vue from 'vue';
import Vuelidate from 'vuelidate';
import axios from 'axios';
import Imask from 'imask';
import {
  $eventBus
} from '../util';
import {
  helpers,
  maxLength,
  minValue,
  maxValue,
  requiredIf
} from 'vuelidate/lib/validators';

/* global _asprhe, _as_rest */
/* eslint camelcase: 0 */

const {
  rest_url,
  rest_nonce,
  customer_hash
} = _as_rest;
const BOILER_TYPES = [
  'Combi',
  'Condensing',
  'Conventional',
  'Energy Efficient Boiler',
  'Regular/Heat Only',
  'System'
];

Vue.prototype.$http = axios;

axios.defaults.headers.common['X-WP-Nonce'] = rest_nonce;
axios.defaults.headers.common['X-Customer-Hash'] = customer_hash;

Vue.use(Vuelidate);

const oneOf = options => value => !helpers.req(value) || options.includes(value);
const requiredIfAnnualBoilerService = function(value) {
  return !this.annual_boiler_service || helpers.req(value);
};
const regex = pattern => value => !helpers.req(value) || pattern.test(value);
const regexIfNotNeverServiced = pattern => function(value) {
  return this.state.boiler.never_serviced || regex(pattern)(value);
};
const lastService = function(value) {
  if (!helpers.req(value) || this.state.boiler.never_serviced || 5 !== value.length) {
    return true;
  }

  let maximum_month = new Date().getMonth() + 1;
  let maximum_year = new Date().getFullYear().toString().substr(-2);

  // eslint-disable-next-line yoda
  if (maximum_month < 10) {
    maximum_month = '0' + maximum_month.toString();
  }

  let maximum = `${maximum_year}/${maximum_month}`;

  return value.replace(/^([0-9]{2})\/([0-9]{2})$/g, '$2/$1') <= maximum;
};
const checked = value => true === value;
const checkedIfAnnualBoilerService = function(value) {
  return !this.annual_boiler_service || true === value;
};

// eslint-disable-next-line no-new
new Vue({
  el: '#as_product-matrix_home-emergency',

  data: {
    order_state: {},
    state: {
      ..._asprhe.state,
      boiler: {
        make: null,
        model: null,
        type: null,
        age: null,
        last_service: null,
        never_serviced: false,
        ..._asprhe.state.boiler
      }
    },
    meta: {
      ..._asprhe.meta,
      loading: true,
      editing: []
    },
    masks: {}
  },

  validations: {
    state: {
      boiler: {
        make: {
          requiredIfAnnualBoilerService,
          maxLength: maxLength(255)
        },
        model: {
          requiredIfAnnualBoilerService,
          maxLength: maxLength(255)
        },
        type: {
          requiredIfAnnualBoilerService,
          oneOf: oneOf(BOILER_TYPES)
        },
        age: {
          requiredIfAnnualBoilerService,
          minValue: minValue(0),
          maxValue: maxValue(25)
        },
        last_service: {
          requiredIf: requiredIf(function(value) {
            if (!this.annual_boiler_service || this.state.boiler.never_serviced) {
              return false;
            }

            return !helpers.req(value.last_service);
          }),
          regexIfNotNeverServiced: regexIfNotNeverServiced(/^(0[1-9]|1[0-2])\/[0-9]{2}$/),
          lastService
        }
      },
      terms_and_conditions: {
        home_emergency: {
          read: {
            checked
          }
        },
        annual_boiler_service: {
          read: {
            checkedIfAnnualBoilerService
          }
        }
      }
    }
  },

  mounted() {
    $eventBus.$emit('mounted', this);
  },

  computed: {
    $matrix: {
      get() {
        return {
          pricing_data: {
            ..._asprhe.pricing_data
          },
          frequencies: {
            ..._asprhe.frequencies
          },
          configurations: {
            ..._asprhe.configurations
          },
          benefit_translations: {
            ..._asprhe.benefit_translations
          },
          boiler_types: [
            ...BOILER_TYPES
          ]
        };
      }
    },
    $defaults: {
      get() {
        return {
          ..._asprhe.defaults
        };
      }
    },

    cover_level() {
      let {
        cover_level
      } = this.state;

      if (1 === this.cover_level_options.length) {
        cover_level = this.cover_level_options[0];
      }

      return cover_level;
    },
    annual_boiler_service() {
      let {
        annual_boiler_service
      } = this.state;

      if (1 === this.annual_boiler_service_options.length) {
        annual_boiler_service = this.annual_boiler_service_options[0];
      }

      return annual_boiler_service;
    },
    excess() {
      let {
        excess
      } = this.state;

      if (1 === this.excess_options.length) {
        excess = this.excess_options[0];
      }

      return excess;
    },
    frequency() {
      let {
        frequency
      } = this.state;

      if (1 === this.frequency_options.length) {
        frequency = this.frequency_options[0];
      }

      return frequency;
    },

    configuration() {
      const data = this.$matrix.pricing_data;
      let {
        frequency
      } = this.state;

      if (!this.cover_level || null === this.annual_boiler_service || !frequency) {
        return false;
      }

      const postId = data[this.cover_level][this.annual_boiler_service ? '1' : '0'] || false;

      if (!postId) {
        return false;
      }

      return this.$matrix.configurations[postId] || false;
    },

    cover_level_options() {
      return Object.keys(this.$matrix.configurations)
        .map(key => this.$matrix.configurations[key])
        .map(configuration => configuration.cover_level.amount)
        .filter((value, index, self) => {
          return index === self.indexOf(value);
        })
        .sort((a, b) => {
          a = parseInt(a, 10);
          b = parseInt(b, 10);

          // eslint-disable-next-line no-nested-ternary
          return a < b ? -1 : (a > b ? 1 : 0);
        })
      ;
    },
    annual_boiler_service_options() {
      if (null === this.cover_level) {
        return [];
      }

      return Object.keys(this.$matrix.configurations)
        .map(key => this.$matrix.configurations[key])
        .filter(configuration => this.cover_level === configuration.cover_level.amount)
        .map(configuration => 1 === configuration.benefits.benefits.annual_boiler_service)
      ;
    },
    excess_options() {
      const configuration = this.configuration;
      const frequency = this.frequency;

      if (false === configuration) {
        return [];
      }

      return (configuration.premiums || [])
        .map(premium => null !== premium[frequency] ? premium.excess : false)
        .filter(value => false !== value)
      ;
    },
    frequency_options() {
      return Object.values(this.$matrix.frequencies);
    },

    premium() {
      const excess = this.excess;
      const frequency = this.frequency;
      const configuration = this.configuration;

      if ((!excess && 0 !== excess) || !frequency || !configuration) {
        return false;
      }

      return configuration
        .premiums
        .filter(premium => null !== premium[frequency])
        .filter(premium => excess === premium.excess)
        .map(premium => premium[frequency])
        .shift()
      ;
    }
  },

  watch: {
    annual_boiler_service(selected) {
      if (false === selected) {
        delete this.masks.last_service;
      }

      this.state.terms_and_conditions.annual_boiler_service = {
        downloaded: false,
        read: false
      };
    },
    cover_level() {
      this.state.terms_and_conditions.home_emergency = {
        downloaded: false,
        read: false
      };
    },
    'order_state.current'(current) {
      if ('product-matrix' === current) {
        setTimeout(() => {
          this.initialise_mask('last_service');

          if (this.masks.last_service) {
            this.$refs.last_service.value = this.state.boiler.last_service;
          }
        }, 1);
      } else {
        delete this.masks.last_service;
      }
    },
    'order_state.customer'() {
      if (false === this.meta.loading) {
        return;
      }

      if (this.order_state.customer && this.order_state.customer.customer_hash) {
        axios.defaults.headers.common['X-Customer-Hash'] = this.order_state.customer.customer_hash;
      }

      if (this.order_state.orderItems &&
        0 < Object.keys(this.order_state.orderItems).length
      ) {
        const orderItem = Object.values(this.order_state.orderItems)[0] || {};
        const metadata = orderItem.metadata || {};
        const boiler = metadata.boiler || {};

        this.init({
          cover_level: metadata.cover_level,
          excess: metadata.excess,
          frequency: metadata.frequency,
          annual_boiler_service: metadata.annual_boiler_service,
          terms_and_conditions: metadata.terms_and_conditions || {},
          boiler: {
            make: boiler.make,
            model: boiler.model,
            type: boiler.type,
            age: boiler.age,
            last_service: boiler.last_service,
            never_serviced: boiler.never_serviced
          }
        });

        return;
      }

      this
        .$http({
          method: 'GET',
          url: `${rest_url}wp-asurit-sales/v1/orders/0`
        })
        .then(({ data }) => {
          const orderItems = {
            ...data['order-items']
          };

          let emit = {
            order: {
              ...data.order
            },
            orderItems,
            payments: {
              ...data.payments
            }
          };

          if (data.order.customer_id && data.customers[data.order.customer_id]) {
            emit.customer = {
              ...data.customers[data.order.customer_id]
            };
          }

          $eventBus.$emit('order-updated', emit);

          return Object
            .keys(orderItems)
            .map(key => orderItems[key])
            .filter(({ product = false }) => 'home-emergency' === product)
            .shift()
            .metadata || {};
        })
        .catch(() => {
          $eventBus.$emit('reset');

          return {};
        })
        .then(({
          cover_level = this.$defaults.cover_level,
          annual_boiler_service = this.$defaults.annual_boiler_service,
          excess = this.$defaults.excess,
          frequency = this.$defaults.frequency,
          boiler = {},
          terms_and_conditions = {}
        }) => {
          this.init({
            cover_level,
            excess,
            frequency,
            annual_boiler_service: annual_boiler_service && (
              true === annual_boiler_service || false === annual_boiler_service
            ) ? annual_boiler_service : null,
            terms_and_conditions: {
              home_emergency: {
                ...terms_and_conditions.home_emergency
              },
              annual_boiler_service: {
                ...terms_and_conditions.annual_boiler_service
              }
            },
            boiler: {
              make: boiler.make || null,
              model: boiler.model || null,
              type: boiler.type || null,
              age: boiler.age || null,
              last_service: boiler.last_service || null,
              never_serviced: boiler.never_serviced || null
            }
          });
        })
      ;
    }
  },

  methods: {
    init({
      cover_level = null,
      excess = null,
      frequency = null,
      annual_boiler_service = null,
      terms_and_conditions = {},
      boiler: {
        make = null,
        model = null,
        type = null,
        age = null,
        last_service = null,
        never_serviced = null
      } = {}
    }) {
      this.state.cover_level = parseInt(cover_level, 10) || null;
      this.state.excess = parseInt(excess, 10) || null;
      this.state.frequency = frequency;

      if (null !== annual_boiler_service) {
        this.state.annual_boiler_service = true === annual_boiler_service;
      }

      if (null === this.state.frequency && 0 !== this.$matrix.frequencies.length) {
        this.state.frequency = this.$matrix.frequencies[0];
      }

      if (this.state.annual_boiler_service) {
        this.state.boiler.make = make;
        this.state.boiler.model = model;
        this.state.boiler.type = type;
        this.state.boiler.age = age;

        if (null !== last_service || null) {
          this.state.boiler.last_service = last_service;

          setTimeout(() => {
            this.initialise_mask('last_service') && (this.masks.last_service.value = last_service);
          }, 100);
        }

        this.state.boiler.never_serviced = never_serviced && true === never_serviced;
      }

      setTimeout(() => {
        // eslint-disable-next-line yoda
        if (typeof terms_and_conditions.home_emergency !== 'undefined') {
          this.state.terms_and_conditions.home_emergency = {
            downloaded: terms_and_conditions.home_emergency.downloaded || false,
            read: terms_and_conditions.home_emergency.read || false
          };
        }

        // eslint-disable-next-line yoda
        if (this.state.annual_boiler_service && typeof terms_and_conditions.annual_boiler_service !== 'undefined') {
          this.state.terms_and_conditions.annual_boiler_service = {
            downloaded: terms_and_conditions.annual_boiler_service.downloaded || false,
            read: terms_and_conditions.annual_boiler_service.read || false
          };
        }
      }, 1);

      this.meta.loading = false;
    },
    initialise_mask(field) {
      let initialised = false;

      if ('last_service' === field && !this.masks.last_service && this.$refs.last_service) {
        this.masks.last_service = new Imask(this.$refs.last_service, {
          mask: '00/00'
        });
        initialised = true;
      }

      return initialised;
    },
    update_last_service() {
      setTimeout(() => {
        this.state.boiler.last_service = this.masks.last_service.value;
      }, 1);
    },
    buy() {
      const cover_level = this.cover_level;
      const annual_boiler_service = this.annual_boiler_service;
      const excess = this.excess;
      const frequency = this.frequency;
      const premium = this.premium;
      const terms_and_conditions = this.state.terms_and_conditions;

      this.$v.state.boiler.make.$touch();
      this.$v.state.boiler.model.$touch();
      this.$v.state.boiler.type.$touch();
      this.$v.state.boiler.age.$touch();
      this.$v.state.boiler.last_service.$touch();
      this.$v.state.terms_and_conditions.home_emergency.read.$touch();
      this.$v.state.terms_and_conditions.annual_boiler_service.read.$touch();

      if (!premium || this.$v.$invalid) {
        $eventBus.$emit('has-error');

        return;
      }

      const metadata = {
        cover_level,
        excess,
        annual_boiler_service,
        frequency,
        terms_and_conditions: {
          home_emergency: {
            ...terms_and_conditions.home_emergency
          }
        }
      };

      if (annual_boiler_service) {
        metadata.boiler = {
          ...this.state.boiler,
          never_serviced: true === this.state.boiler.never_serviced
        };
        metadata.terms_and_conditions.annual_boiler_service = {
          ...terms_and_conditions.annual_boiler_service
        };
      }

      let orderId = 0;

      if (this.order_state.order && this.order_state.order.id) {
        orderId = this.order_state.order.id;
      }

      this
        .$http({
          method: 'POST',
          url: `${rest_url}wp-asurit-sales/v1/orders/${orderId}/products`,
          data: {
            product: 'home-emergency',
            metadata
          }
        })
        .then(({ data }) => {
          let emit = {
            completed: 'product-matrix',
            order: {
              ...data.order
            },
            orderItems: {
              ...data['order-items']
            },
            payments: {
              ...data.payments
            }
          };

          if (data.order.customer_id && data.customers[data.order.customer_id]) {
            emit.customer = {
              ...data.customers[data.order.customer_id]
            };
          }

          $eventBus.$emit('order-updated', emit);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log(error.message || error);
        })
      ;
    },
    edit() {
      $eventBus.$emit('order-updated', { edit: 'product-matrix' });
    },
    setEditing(key, value = false) {
      if (value) {
        if (!this.meta.editing.includes(key)) {
          this.meta.editing.push(key);
        }
      } else if (this.meta.editing.includes(key)) {
        let index = this.meta.editing.indexOf(key);

        this.meta.editing.splice(index, 1);
      }
    },
    isEditing(key) {
      return this.meta.editing.includes(key);
    }
  }
});
